import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetAgreementImportsBySessionIdContractsQuery } from '@client/api';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  Typography,
} from '@client/toolkit';

import { ContractStatusBadge } from './contract-status-badge';

interface StepContractsProps {
  sessionId: string;
  onReviewContract: (contractId: string) => void;
  onContinueToApprove: () => void;
  onBack: () => void;
}

/**
 * Step 3 — overview of every contract staged in this session (result of Phase 2/2b mapping).
 * Clicking a row opens Step 4 (review & correct) for that contract.
 */
export function StepContracts({ sessionId, onReviewContract, onContinueToApprove, onBack }: StepContractsProps) {
  const { t: tAgreements } = useTranslation('agreements');
  const { t: tCommon } = useTranslation('common');

  const { data: contracts, isLoading, isFetching } = useGetAgreementImportsBySessionIdContractsQuery({ sessionId });

  const items = useMemo(() => contracts ?? [], [contracts]);
  const isEmpty = !isLoading && items.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle variant="highlighted">{tAgreements('agreementImport.contracts.title')}</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {!isLoading && (
          <Typography variant="secondary">
            {tAgreements('agreementImport.contracts.description', { count: items.length })}
          </Typography>
        )}

        <Table className="overflow-hidden rounded-md">
          {isEmpty && (
            <caption className="px-4 py-4 [caption-side:bottom]">
              <EmptyPlaceholder>{tAgreements('agreementImport.contracts.empty')}</EmptyPlaceholder>
            </caption>
          )}
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{tAgreements('agreementImport.contracts.documentId')}</TableHeadCell>
              <TableHeadCell>{tAgreements('agreementImport.contracts.tenant')}</TableHeadCell>
              <TableHeadCell>{tAgreements('agreementImport.contracts.lines')}</TableHeadCell>
              <TableHeadCell>{tAgreements('agreementImport.contracts.status')}</TableHeadCell>
              <TableHeadCell />
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading &&
              Array.from(Array(3).keys()).map((x) => (
                <TableRow key={x}>
                  <TableCell colSpan={5}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))}

            {!isLoading &&
              items.map((contract) => (
                <TableRow key={contract.id} verticalAlign="middle">
                  <TableCell>{contract.documentId}</TableCell>
                  <TableCell>{contract.rawSourceData.tenantName ?? '—'}</TableCell>
                  <TableCell>{contract.lines.length}</TableCell>
                  <TableCell>
                    <ContractStatusBadge status={contract.status} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="xs" color="primary-invert" onClick={() => onReviewContract(contract.id)}>
                      {tAgreements('agreementImport.contracts.reviewButton')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>

        <div className="flex justify-between">
          <Button color="primary-dark" onClick={onBack}>
            {tCommon('back')}
          </Button>
          <Button color="primary" disabled={items.length === 0 || isFetching} onClick={onContinueToApprove}>
            {tAgreements('agreementImport.contracts.continueButton')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
